import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Badge,
  useToast,
  useColorModeValue,
  Checkbox,
  Alert,
  AlertIcon,
  SimpleGrid,
  Divider,
  Switch,
  FormControl,
  FormLabel,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { FiSave, FiRotateCcw } from 'react-icons/fi';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';
import { useAuth } from '../../../contexts/AuthContext';

// Default workflow — Arive-aligned 1:1 with LOS pipeline as of 2026-05-20.
// Forward-only by default; deal-lost always available as escape hatch.
const DEFAULT_WORKFLOW = {
  transitions: {
    // Lead funnel
    'lead-new':                  ['lead-contacted', 'lead-quoted', 'loan-application-intake', 'deal-lost'],
    'lead-contacted':            ['lead-quoted', 'loan-application-intake', 'deal-lost'],
    'lead-quoted':               ['loan-application-intake', 'deal-lost'],

    // Loan workflow — linear with skip-forward allowed
    'loan-application-intake':   ['loan-qualification', 'loan-preapproved', 'deal-lost'],
    'loan-qualification':        ['loan-preapproved', 'loan-setup', 'deal-lost'],
    'loan-preapproved':          ['loan-setup', 'loan-disclosure-sent', 'deal-lost'],
    'loan-setup':                ['loan-disclosure-sent', 'loan-submitted', 'deal-lost'],
    'loan-disclosure-sent':      ['loan-submitted', 'deal-lost'],
    'loan-submitted':            ['loan-approved-conditions', 'loan-resubmittal', 'loan-clear-to-close', 'deal-lost'],
    'loan-approved-conditions':  ['loan-resubmittal', 'loan-clear-to-close', 'deal-lost'],
    'loan-resubmittal':          ['loan-submitted', 'loan-approved-conditions', 'deal-lost'],
    'loan-clear-to-close':       ['loan-docs-out', 'deal-lost'],
    'loan-docs-out':             ['loan-docs-signed', 'deal-lost'],
    'loan-docs-signed':          ['loan-funded', 'deal-lost'],
    'loan-funded':               ['loan-brokercheck-received', 'loan-finalized'],
    'loan-brokercheck-received': ['loan-finalized'],
    'loan-finalized':            [], // terminal-positive
    'deal-lost':                 [], // terminal-negative

    // Legacy stages — allow transitions OUT only. Not selectable as targets in
    // any active flow, but deals already in these stages can still advance.
    'lead-contacted-hot':        ['lead-contacted', 'lead-quoted', 'deal-lost'],
    'lead-qualified':            ['lead-quoted', 'loan-application-intake', 'deal-lost'],
    'lead-qualified-hot':        ['lead-quoted', 'loan-application-intake', 'deal-lost'],
    'lead-preapproved-legacy':   ['loan-preapproved', 'loan-application-intake', 'deal-lost'],
    'lead-preapproved-shopper':  ['loan-preapproved', 'deal-lost'],
  },
  allowBackwards: false,
  allowSkipping: true,
};

export default function StageWorkflowEditor() {
  const { currentUser } = useAuth();
  const { dealStages, loading: settingsLoading } = useCrmSettings();
  const toast = useToast();
  const [workflow, setWorkflow] = useState(DEFAULT_WORKFLOW);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBG = useColorModeValue('gray.50', 'gray.700');
  
  // Use dealStages from context instead of hardcoded STAGES
  const STAGES = dealStages || [];

  useEffect(() => {
    loadWorkflow();
  }, []);

  const loadWorkflow = async () => {
    setLoading(true);
    try {
      const workflowDoc = await getDoc(doc(db, 'config', 'stageTransitions'));
      
      if (workflowDoc.exists()) {
        setWorkflow({ ...DEFAULT_WORKFLOW, ...workflowDoc.data() });
      } else {
        setWorkflow(DEFAULT_WORKFLOW);
      }
    } catch (error) {
      console.error('Error loading workflow:', error);
      toast({
        title: 'Error loading workflow',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const workflowRef = doc(db, 'config', 'stageTransitions');
      await setDoc(workflowRef, {
        ...workflow,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      });

      toast({
        title: 'Workflow saved',
        description: 'Stage transition rules updated',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error saving workflow:', error);
      toast({
        title: 'Error saving workflow',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset to default workflow? This will override your custom rules.')) {
      return;
    }

    setSaving(true);
    try {
      const workflowRef = doc(db, 'config', 'stageTransitions');
      await setDoc(workflowRef, {
        ...DEFAULT_WORKFLOW,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      });

      setWorkflow(DEFAULT_WORKFLOW);

      toast({
        title: 'Workflow reset',
        description: 'Restored to default configuration',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error resetting workflow:', error);
      toast({
        title: 'Error resetting workflow',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleTransition = (fromStage, toStage) => {
    setWorkflow((prev) => {
      const newTransitions = { ...prev.transitions };
      const currentTransitions = newTransitions[fromStage] || [];

      if (currentTransitions.includes(toStage)) {
        // Remove transition
        newTransitions[fromStage] = currentTransitions.filter((s) => s !== toStage);
      } else {
        // Add transition
        newTransitions[fromStage] = [...currentTransitions, toStage];
      }

      return { ...prev, transitions: newTransitions };
    });
  };

  if (loading) {
    return (
      <Center py={12}>
        <VStack>
          <Spinner size="lg" />
          <Text>Loading workflow...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between">
          <VStack align="flex-start" spacing={0}>
            <Heading size="md">Stage Workflow Configuration</Heading>
            <Text fontSize="sm" color="gray.600">
              Define which stage transitions are allowed
            </Text>
          </VStack>
          <HStack>
            <Button
              leftIcon={<FiRotateCcw />}
              size="sm"
              variant="outline"
              onClick={handleReset}
            >
              Reset to Default
            </Button>
            <Button
              leftIcon={<FiSave />}
              colorScheme="blue"
              size="sm"
              onClick={handleSave}
              isLoading={saving}
              loadingText="Saving..."
            >
              Save Workflow
            </Button>
          </HStack>
        </HStack>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Select which stage transitions are allowed. Unchecked transitions will be blocked
            with a helpful error message. The "Deal Lost" option is always available.
          </Text>
        </Alert>

        {/* Global Settings */}
        <Box p={4} bg={cardBG} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
          <VStack align="stretch" spacing={3}>
            <FormControl display="flex" alignItems="center">
              <FormLabel fontSize="sm" mb={0}>
                Allow Backwards Movement
              </FormLabel>
              <Switch
                isChecked={workflow.allowBackwards}
                onChange={(e) => setWorkflow({ ...workflow, allowBackwards: e.target.checked })}
                colorScheme="blue"
              />
            </FormControl>
            <Text fontSize="xs" color="gray.600">
              If enabled, users can move deals back to earlier stages (e.g., Qualified → Contacted)
            </Text>

            <Divider />

            <FormControl display="flex" alignItems="center">
              <FormLabel fontSize="sm" mb={0}>
                Allow Skipping Stages
              </FormLabel>
              <Switch
                isChecked={workflow.allowSkipping}
                onChange={(e) => setWorkflow({ ...workflow, allowSkipping: e.target.checked })}
                colorScheme="blue"
              />
            </FormControl>
            <Text fontSize="xs" color="gray.600">
              If enabled, users can skip stages (e.g., New → Qualified without Contacted)
            </Text>
          </VStack>
        </Box>

        {/* Transition Matrix */}
        <Box>
          <Text fontWeight="semibold" mb={3}>
            Allowed Transitions by Stage
          </Text>
          <VStack align="stretch" spacing={4}>
            {STAGES.filter(s => !s.isLegacy && !['loan-funded', 'loan-brokercheck-received', 'loan-finalized', 'deal-lost'].includes(s.value)).map((fromStage) => (
              <Box
                key={fromStage.value}
                p={4}
                bg={cardBG}
                borderRadius="md"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <VStack align="stretch" spacing={3}>
                  <HStack>
                    <Badge colorScheme={fromStage.color} fontSize="sm">
                      {fromStage.label}
                    </Badge>
                    <Text fontSize="sm" color="gray.600">
                      can move to:
                    </Text>
                  </HStack>

                  <SimpleGrid columns={3} spacing={2}>
                    {STAGES.filter((toStage) => toStage.value !== fromStage.value).map((toStage) => (
                      <Checkbox
                        key={toStage.value}
                        isChecked={(workflow.transitions[fromStage.value] || []).includes(toStage.value)}
                        onChange={() => toggleTransition(fromStage.value, toStage.value)}
                        colorScheme="blue"
                        size="sm"
                      >
                        <Text fontSize="sm">{toStage.label}</Text>
                      </Checkbox>
                    ))}
                  </SimpleGrid>
                </VStack>
              </Box>
            ))}
          </VStack>
        </Box>

        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Changes take effect immediately after saving. Test transitions carefully before
            rolling out to all users.
          </Text>
        </Alert>
      </VStack>
    </Box>
  );
}

